import React from 'react';
// Updated imports for new types
import { ID, AppTheme, AppAspect, TaxonomyStoreState, SubTheme } from './types';
import './styles.css';

interface DetailsDrawerProps {
    theme: AppTheme | null; // Changed from themeId to the full theme object or null
    selectedPath: TaxonomyStoreState['path'];
    onClose: () => void;
    allThemes: AppTheme[]; // Passed from TaxonomyExplorer, might be useful for context
    allAspects: AppAspect[]; // Passed from TaxonomyExplorer, useful for breadcrumb/path display
}

const DetailsDrawer: React.FC<DetailsDrawerProps> = ({
    theme,
    selectedPath,
    onClose,
    allAspects, // Destructure allAspects for path display
    // allThemes is available if needed for more complex sub-theme scenarios later
}) => {

    if (!theme) {
        // This case should ideally be handled by the CSSTransition unmountOnExit in TaxonomyExplorer
        // but as a fallback:
        return (
            <div className="details-drawer bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-md p-0">
                <div className="p-4">
                    <button
                        onClick={onClose}
                        className="float-right text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        ✕
                    </button>
                    <p className="text-gray-600 dark:text-gray-300">No theme selected.</p>
                </div>
            </div>
        );
    }

    // Construct the full path string using selectedPath and allAspects
    const pathParts: string[] = [];
    if (selectedPath.l1) {
        const l1Aspect = allAspects.find(a => a.name === selectedPath.l1);
        if (l1Aspect) pathParts.push(l1Aspect.name);
    }
    if (selectedPath.l2) {
        const l2Aspect = allAspects.find(a => a.name === selectedPath.l2);
        if (l2Aspect) pathParts.push(l2Aspect.name);
    }
    if (selectedPath.l3) {
        const l3Aspect = allAspects.find(a => a.name === selectedPath.l3);
        if (l3Aspect) pathParts.push(l3Aspect.name);
    }
    pathParts.push(theme.name); // Add current theme name to path

    const pathString = pathParts.join(' > ');

    return (
        <div className="details-drawer bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-md flex flex-col h-full overflow-hidden">
            <div className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{theme.name}</h2>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    aria-label="Close details panel"
                >
                    <span className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">✕</span>
                </button>
            </div>

            <div className="p-5 overflow-y-auto flex-grow">
                <div className="mb-5">
                    {theme.description &&
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{theme.description}</p>
                    }

                    <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 my-4">
                        <h4 className="text-sm font-medium mb-2 text-gray-500 dark:text-gray-400">Path</h4>
                        <p className="text-sm mb-2 text-gray-700 dark:text-gray-200">{pathString}</p>

                        <h4 className="text-sm font-medium mt-3 mb-1 text-gray-500 dark:text-gray-400">Parent Aspect</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                            {theme.parent_aspect_name || theme.parentAspectName}
                            <span className="text-xs text-gray-500 dark:text-gray-400"> ({theme.parentAspectLevel})</span>
                        </p>
                        {theme.grandparent_aspect_name && (
                            <>
                                <h4 className="text-sm font-medium mt-2 mb-1 text-gray-500 dark:text-gray-400">Grandparent Aspect</h4>
                                <p className="text-sm text-gray-700 dark:text-gray-200">{theme.grandparent_aspect_name}</p>
                            </>
                        )}
                    </div>
                </div>

                {theme.sub_themes && theme.sub_themes.length > 0 && (
                    <div className="mb-4">
                        <h3 className="text-md font-semibold mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">Sub-themes ({theme.sub_themes.length})</h3>
                        <ul className="space-y-3">
                            {theme.sub_themes.map((subTheme: SubTheme) => (
                                <li key={subTheme.name} className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md border border-gray-200 dark:border-gray-700">
                                    <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">{subTheme.name}</h4>
                                    {subTheme.description &&
                                        <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">{subTheme.description}</p>
                                    }
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {(!theme.sub_themes || theme.sub_themes.length === 0) && (
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-md">
                        <p className="text-gray-500 dark:text-gray-400 italic">No sub-themes for this theme</p>
                    </div>
                )}

                {/* Placeholder for metrics tables with nicer styling */}
                <div className="mt-8 p-4 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900">
                    <h3 className="text-md font-semibold mb-3 text-gray-900 dark:text-gray-100">Metrics and Data</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center p-4">
                        (Metrics tables and additional data will be displayed here)
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DetailsDrawer;